import React, { useState, useEffect, useCallback } from "react";
import { ContentSectionHeader, PageHeader } from "../../../../components/common";
import { Plus, RefreshCw } from "lucide-react";
import { Button } from "../../../../components";
import { useToast } from "../../../../components";
import { useTranslation } from "react-i18next";
import { ConfirmationDialog } from "../../../../components/confirmation-dialog";
import { useNavigate } from "react-router-dom";
import { EventFilters, EventListForAdminResponse } from "../types/event.types";
import { EventListView } from "../components/event-table";
import { EventQRCodeModal } from "../components/EventQRCodeModal";
import EventStatsSection from "../components/EventStatsSection";
import { getEventStatus } from "../utils/event.utils";
import { getEventQrImage } from "../api/event.api";
import { getApiErrorMessage } from "../../../../lib/result-codes";
import { useDeleteEvent, useEventOptions, useEventStatistics, useGetEventList } from "../hooks";
import { useAuthSession } from "../../../auth/shared";
import { ReactComponent as TotalUsersIcon } from "../assets/icons/total-users.svg";
import { ReactComponent as NewUsersWeekIcon } from "../assets/icons/new-users-week.svg";
import { ReactComponent as EventsWeekIcon } from "../assets/icons/events-week.svg";
import { ReactComponent as CouponsExchangedIcon } from "../assets/icons/coupons-exchanged.svg";

type EventListPageProps = {
  basePath?: string;
};

function EventListPage({ basePath = "/event" }: EventListPageProps) {
  const { user } = useAuthSession();
  const { t } = useTranslation();
  const {
    events,
    isLoading,
    isError,
    error,
    refreshEvents,
    request,
    haveFilter,
    setFilter,
    setPagination,
    setSorting,
  } = useGetEventList();
  const { deleteEvent, loading: isDeleting } = useDeleteEvent();
  const {
    categoryOptions,
    fetchCategoryOptions,
    fetchSubCategoryOptions,
  } = useEventOptions(user?.organization_id);
  const [activeFilterPopup, setActiveFilterPopup] = useState<keyof EventFilters | null>(null);

  const isFilterPopupOpen = useCallback(
    (filterKey: keyof EventFilters) => {
      return activeFilterPopup === filterKey;
    },
    [activeFilterPopup],
  );

  const handleFilterPopupChange = useCallback((filterKey: keyof EventFilters, open: boolean) => {
    if (open) {
      setActiveFilterPopup(filterKey);
    } else {
      setActiveFilterPopup(null);
    }
  }, []);

  const {
    data: eventStats,
    loading: statsLoading,
    refresh: refreshEventStats,
  } = useEventStatistics();
  const { addToast } = useToast();
  const [eventToDelete, setEventToDelete] = useState<EventListForAdminResponse | null>(null);
  const itemsPerPage = 20;
  const totalPages = Math.ceil(request.pagination.totalCount / itemsPerPage);

  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedEventForQR, setSelectedEventForQR] = useState<EventListForAdminResponse | null>(
    null,
  );

  const navigate = useNavigate();
  const handleDateSort = useCallback(
    (nextDirection: "asc" | "desc" | null) => {
      setSorting(
        nextDirection ? { sort_key: "event_date", sort_direction: nextDirection } : undefined,
      );
    },
    [setSorting],
  );

  const handleDeleteEvent = (event: EventListForAdminResponse) => {
    setEventToDelete(event);
  };

  const handleConfirmDelete = async () => {
    if (!eventToDelete) return;

    const eventStatus = getEventStatus(eventToDelete);

    if (eventStatus === "completed") {
      addToast({
        type: "error",
        title: "Cannot Delete Event",
        message: "You cannot delete this event because it has been completed",
      });

      setEventToDelete(null);
      return;
    }

    try {
      await deleteEvent(eventToDelete.id);
      await refreshEvents();
      await refreshEventStats();
      addToast({
        type: "success",
        title: "Success!",
        message: "Event has been deleted successfully",
      });
      setEventToDelete(null);
    } catch (deleteError: unknown) {
      addToast({
        type: "error",
        title: "Error!",
        message: getApiErrorMessage(deleteError, "An error occurred while deleting the event"),
      });
    } finally {
    }
  };

  const handleRefreshAll = () => {
    refreshEvents();
    refreshEventStats();
  };

  const handleOpenQRCodePopup = (event: EventListForAdminResponse) => {
    setSelectedEventForQR(event);
    setQrModalOpen(true);
  };

  const statIconFrame = (icon: React.ReactNode) => (
    <div className="w-6 h-6 p-1 rounded-md flex items-center justify-center bg-[#F2FCFA] dark:bg-emerald-900/30">
      {icon}
    </div>
  );

  const stats = [
    {
      label: t("event.total_events"),
      value: eventStats?.total_event_count ?? request.pagination.totalCount,
      icon: statIconFrame(<TotalUsersIcon className="h-[18px] w-[18px]" />),
    },
    {
      label: t("event.upcoming_events"),
      value:
        eventStats?.upcoming_count ?? events.filter((e) => getEventStatus(e) === "upcoming").length,
      icon: statIconFrame(<NewUsersWeekIcon className="h-[18px] w-[18px]" />),
    },
    {
      label: t("event.ongoing_events"),
      value:
        eventStats?.ongoing_count ?? events.filter((e) => getEventStatus(e) === "ongoing").length,
      icon: statIconFrame(<EventsWeekIcon className="h-[18px] w-[18px]" />),
    },
    {
      label: t("event.completed_events"),
      value:
        eventStats?.completed_count ??
        events.filter((e) => getEventStatus(e) === "completed").length,
      icon: statIconFrame(<CouponsExchangedIcon className="h-[18px] w-[18px]" />),
    },
  ];

  useEffect(() => {
    fetchCategoryOptions();
    fetchSubCategoryOptions();
  }, [fetchCategoryOptions, fetchSubCategoryOptions]);

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title={t("event.management")}
        subtitle={t("event.subtitle")}
        sticky
        actions={
          <Button
            variant="primary"
            onClick={() => navigate(`${basePath}/create`)}
          >
            <Plus className="h-4 w-4" />
            {t("event.add_new_event")}
          </Button>
        }
      />

      <div className="px-6 py-6">
        <EventStatsSection
          stats={stats}
          loading={statsLoading}
        />

        <ContentSectionHeader
          title={t("event.event_list")}
          actions={
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefreshAll}
              disabled={isLoading || statsLoading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading || statsLoading ? "animate-spin" : ""}`} />
              {haveFilter ? t("common.clear") : t("ui.refresh")}
            </Button>
          }
        />

        <EventListView
          loading={isLoading}
          error={isError ? error : null}
          onRetry={refreshEvents}
          events={events}
          filters={request.filters}
          updateFilter={setFilter}
          dateSortDirection={request.sorting?.sort_direction ?? null}
          onDateSort={handleDateSort}
          categoryOptions={categoryOptions}
          isFilterPopupOpen={(key) => isFilterPopupOpen(key as keyof EventFilters)}
          handleFilterPopupChange={(key, open) =>
            handleFilterPopupChange(key as keyof EventFilters, open)
          }
          onRowOpen={(eventItem) => navigate(`${basePath}/view/${eventItem.id}`)}
          onEdit={(eventItem) => navigate(`${basePath}/view/${eventItem.id}`)}
          onDelete={(eventItem) => {
            handleDeleteEvent(eventItem);
          }}
          onQRCode={handleOpenQRCodePopup}
          getEventStatus={getEventStatus}
          page={request.pagination.page}
          totalPages={totalPages}
          onPageChange={(value) => setPagination((prev) => ({ ...prev, page: value }))}
        />
      </div>

      <ConfirmationDialog
        isOpen={Boolean(eventToDelete)}
        onClose={() => setEventToDelete(null)}
        onConfirm={handleConfirmDelete}
        title={t("event.delete_event")}
        description={t("event.delete_confirm", { title: eventToDelete?.title })}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        isLoading={isDeleting}
        type="danger"
      />

      {qrModalOpen && (
        <EventQRCodeModal
          open={qrModalOpen}
          onClose={() => {
            setQrModalOpen(false);
            setSelectedEventForQR(null);
          }}
          eventId={selectedEventForQR?.id || ""}
          eventName={selectedEventForQR?.title || ""}
          fetchQrImage={getEventQrImage}
        />
      )}
    </div>
  );
}

export default EventListPage;
